import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import { setLoadingMessage } from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addSceneVideo,
  addVideo,
  setPushIntersectedObject,
  selected_object,
  set_is_video_editoropen,
  setVideoScales,
  update_assets_data,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import { copyPhysicsToDuplicate } from '../../utils/Physics/PhysicsUtils';
import {
  calculateVideoPlaneDimensions,
  DEFAULT_VIDEO_ASPECT_MODE,
  resolveVideoPlaneDimensions,
  videoSavedScaleToMeshScale,
} from '../../utils/videoScaleUtils';
import { applyStoredPivotToObject } from '../../utils/ThreeJsUtils/pivotEditor';
// import { redoStack, undoStack } from './UndoReedo';



export function handleVideoUpload(
  e,
  { Three, scene, transformControls, selectedObject, setPolyCount },
  setPendingUploads
) {
  if (!e.target.files || e.target.files.length === 0) {
    return;
  }

  const file = e.target.files[0];
  const maxSize = 10 * 1024 * 1024; // 10MB size limit

  if (file.size > maxSize) {
    toast.error(
      () => (
        <div className="flex justify-between items-center">
          <span>File size should not exceed 10MB.</span>
        </div>
      ),
      {
        duration: 3000,
        style: {
          background: '#1d1e21',
          color: '#fff',
        },
      }
    );
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls.detach();
  store.dispatch(setLoadingMessage(true));

  const objectURL = URL.createObjectURL(file);
  const tempId = `temp-${Date.now()}-${file.name}`;
  const videoElement = document.createElement('video');

  videoElement.src = objectURL;
  videoElement.autoplay = false;
  videoElement.crossOrigin = 'anonymous';
  videoElement.load();

  videoElement.addEventListener('loadeddata', async () => {
    const videoW = videoElement.videoWidth;
    const videoH = videoElement.videoHeight;
    const aspectRatio = videoW / videoH;

    const { planeWidth, planeHeight } = calculateVideoPlaneDimensions(
      aspectRatio,
      DEFAULT_VIDEO_ASPECT_MODE
    );

    const videoTexture = new Three.VideoTexture(videoElement);
    videoTexture.minFilter = Three.LinearFilter;
    videoTexture.magFilter = Three.LinearFilter;
    videoTexture.generateMipmaps = false;

    const material = new Three.MeshStandardMaterial({
      map: videoTexture,
      side: Three.DoubleSide,
    });

    const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
    const videoMesh = new Three.Mesh(geometry, material);
    videoMesh.castShadow = true;
    videoMesh.receiveShadow = true;
    store.dispatch(setVideoScales({ x: planeWidth, y: planeHeight }));

    // Set initial orientation
    videoMesh.rotation.order = 'XYZ';
    videoMesh.rotation.set(0, 0, 0);
    videoMesh.scale.set(1, 1, 1);

    // ✅ FIXED: Use tempId for lookups, file.name for display
    videoMesh.name = tempId; // For scene.getObjectByName() lookups
    videoMesh._id = tempId; // Unique identifier
    videoMesh.url = tempId; // For backward compatibility
    videoMesh.objPath = file;
    videoMesh.file_name = file.name; // ✅ Actual filename for display
    videoMesh.deleteType = 'video';
    videoMesh.asset_file_path = objectURL;
    videoMesh.originalFile = file;
    videoMesh.localFile = file;
    videoMesh.temp_file_name = tempId; // ✅ Keep temp ID separate
    videoMesh.videoElement = videoElement;
    videoMesh.videoTexture = videoTexture;

    // ✅ Store file_name in userData
    videoMesh.userData = {
      file_name: file.name, // ✅ For display purposes
      video_settings: {
        video_source: objectURL,
        play_on_awake: true,
        aspect_ratio: DEFAULT_VIDEO_ASPECT_MODE,
        base_plane_width: planeWidth,
        base_plane_height: planeHeight,
      },
    };

    // Add cleanup method
    videoMesh.dispose = function () {
      if (this.videoElement) {
        this.videoElement.pause();
        this.videoElement.src = '';
        this.videoElement.load();
      }
      if (this.videoTexture) {
        this.videoTexture.dispose();
      }
      if (this.material) {
        this.material.dispose();
      }
      if (this.geometry) {
        this.geometry.dispose();
      }
    };

    if (setPendingUploads) {
      setPendingUploads((prev) => [
        ...prev,
        {
          file: file,
          transform: {
            position_x: 0,
            position_y: 0,
            position_z: 0,
            rotation_x: 0,
            rotation_y: 0,
            rotation_z: 0,
            scale_x: 1,
            scale_y: 1,
            scale_z: 1,
          },
          tempId: tempId,
        },
      ]);
    }

    store.dispatch(setPushIntersectedObject(videoMesh));
    scene.add(videoMesh);
    videoMesh.objectURL = objectURL;

    selectedObject = videoMesh;
    setPolyCount((prev) => prev + 1);
    selectObjectInScene(tempId);

    toast.success('Video added to scene successfully');

    store.dispatch(addSceneVideo(videoMesh));
    store.dispatch(addVideo({ url: objectURL, name: file.name }));
    store.dispatch(add_new_assets({ type: 'video', data: videoMesh }));
    store.dispatch(set_is_video_editoropen(true));
    store.dispatch(setLoadingMessage(false));
  });

  videoElement.addEventListener('error', (e) => {
    // If the error occurred because the src was cleared (intentional cleanup), ignore it
    if (!videoElement.src || videoElement.src === window.location.href) {
      return;
    }
    toast.error('Failed to load video file');
    store.dispatch(setLoadingMessage(false));
  });
}

export function DownloadVideo(
  videoData,
  { video, Three, scene, selectedObject, setPolyCount }
) {
  // First cleanup any existing videos
  scene.children.forEach((child) => {
    if (child.deleteType === 'video') {
      cleanupVideoResources(child);
    }
  });

  // Helper function to set up video with proper source
  function setupVideoSource(video, videoPath) {
    return new Promise((resolve, reject) => {
      video.src = videoPath;
      video.addEventListener(
        'loadedmetadata',
        () => {
          resolve();
        },
        { once: true }
      );
      video.addEventListener(
        'error',
        (e) => {
          reject(
            new Error(
              `Video loading error: ${video.error?.message || 'unknown'}`
            )
          );
        },
        { once: true }
      );
    });
  }

  videoData.forEach((_videoData) => {
    const videoPath = _videoData.asset_file_path;

    const position_x = _videoData.position_x;
    const position_y = _videoData.position_y;
    const position_z = _videoData.position_z;

    const rotation_x = _videoData.rotation_x;
    const rotation_y = _videoData.rotation_y;
    const rotation_z = _videoData.rotation_z;

    const scale_x = _videoData.scale_x;
    const scale_y = _videoData.scale_y;
    const scale_z = _videoData.scale_z;

    store.dispatch(addVideo({ url: videoPath, name: videoPath }));

    video = document.createElement('video');
    if (video) {
      // Autoplay-related attributes
      video.autoplay = _videoData?.video_settings?.play_on_awake ?? false;
      video.muted = true; // Required for autoplay in most browsers
      video.playsInline = true; // Important for iOS
      video.crossOrigin = 'anonymous';
      video.preload = 'auto';

      // Set up the texture that will be updated with video content
      const videoTexture = new Three.VideoTexture(video);
      videoTexture.minFilter = Three.LinearFilter;
      videoTexture.magFilter = Three.LinearFilter;

      // Load and setup the video source
      setupVideoSource(video, videoPath)
        .then(() => {
          // Now that the video metadata is loaded
          const videoW = video.videoWidth || 640;
          const videoH = video.videoHeight || 360;
          const videoAspect = videoW / videoH;
          const aspectMode =
            _videoData.video_settings?.aspect_ratio ?? DEFAULT_VIDEO_ASPECT_MODE;
          const { planeWidth: planeWidthv, planeHeight: planeHeightv } =
            resolveVideoPlaneDimensions({
              videoAspect,
              aspectRatioMode: aspectMode,
              savedPlaneWidth: _videoData.video_settings?.base_plane_width,
              savedPlaneHeight: _videoData.video_settings?.base_plane_height,
            });

          // Generate a placeholder thumbnail while video is loading
          const canvas = document.createElement('canvas');
          canvas.width = videoW;
          canvas.height = videoH;
          const ctx = canvas.getContext('2d');

          // Draw a placeholder if we can't get a frame from the video yet
          if (video.readyState < 2) {
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#FFFFFF';
            ctx.font = '20px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(
              'Video loading...',
              canvas.width / 2,
              canvas.height / 2
            );
          } else {
            // Try to get a frame if video is ready
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          }

          const video_material = new Three.MeshStandardMaterial({
            map: videoTexture,
            side: Three.DoubleSide,
          });

          const geometryVideo = new Three.PlaneGeometry(
            planeWidthv,
            planeHeightv
          );
          const planeVideo = new Three.Mesh(geometryVideo, video_material);
          planeVideo.castShadow = true;
          planeVideo.receiveShadow = true;

          planeVideo.name = _videoData._id;
          planeVideo.url = _videoData._id;
          planeVideo.file_name = _videoData.file_name;
          planeVideo.deleteType = 'video';
          // Use the correct persisted path key
          planeVideo.asset_file_path = _videoData.asset_file_path;
          planeVideo.rotation.order = 'XYZ';
          if (!planeVideo.userData) {
            planeVideo.userData = {};
          }
          planeVideo.userData.file_name = _videoData.file_name;

          // Store video element reference to control playback later
          planeVideo.videoElement = video;

          planeVideo.position.set(position_x, position_y, position_z);
          const meshScale = videoSavedScaleToMeshScale(
            scale_x,
            scale_y,
            scale_z,
            planeWidthv,
            planeHeightv
          );
          planeVideo.scale.set(meshScale.x, meshScale.y, meshScale.z);
          planeVideo.rotation.set(
            Three.MathUtils.degToRad(rotation_x),
            Three.MathUtils.degToRad(rotation_y),
            Three.MathUtils.degToRad(rotation_z)
          );

          planeVideo.userData.video_settings = {
            ...(_videoData.video_settings || {}),
            aspect_ratio: aspectMode,
            base_plane_width: planeWidthv,
            base_plane_height: planeHeightv,
          };

          applyStoredPivotToObject(planeVideo, _videoData, {
            projectId:
              typeof window !== 'undefined'
                ? window.location?.pathname?.split('/').filter(Boolean).pop()
                : null,
            cacheKeys: [
              _videoData._id,
              _videoData.id,
              _videoData.name,
              _videoData.temp_file_name,
              _videoData.file_name,
              _videoData.url,
            ],
          });

          planeVideo.Events = _videoData.events;
          planeVideo.events = _videoData.events;

          // Apply video settings to the video element
          if (_videoData.video_settings) {
            const settings = _videoData.video_settings;
            video.muted = settings.mute ?? false;
            video.volume = settings.volume ?? 0.5;
            video.loop = settings.loop ?? false;
            video.playbackRate = settings.playback_speed ?? 1;
          }
          planeVideo.temp_file_name = _videoData.temp_file_name;

          scene.add(planeVideo);
          // undoStack.push({
          //   type: 'add',
          //   object: planeVideo,
          //   arrKey: 'video',
          //   data: {
          //     position: planeVideo.position.clone(),
          //     rotation: planeVideo.rotation.clone(),
          //     scale: planeVideo.scale.clone(),
          //   },
          // });
          // redoStack.length = 0;

          // Push values to respective arrays
          store.dispatch(setVideoScales({ x: planeWidthv, y: planeHeightv }));

          selectedObject = planeVideo;
          store.dispatch(setPushIntersectedObject(planeVideo));
          setPolyCount((prevCount) => prevCount + 1);

          // Select it in UI
          if (typeof selectObjectInScene === 'function') {
            selectObjectInScene(_videoData._id);
          }
          store.dispatch(addSceneVideo(planeVideo));
          store.dispatch(selected_object(planeVideo));

          // Attempt to autoplay if needed
          const playOnAwake = _videoData?.video_settings?.play_on_awake;
          if (playOnAwake === true || String(playOnAwake) === 'true') {
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise
                .then(() => {})
                .catch((error) => {
                  console.warn(
                    'Autoplay prevented for video:',
                    videoPath,
                    error
                  );
                });
            }
          }
        })
        .catch((error) => {
          console.error('Error setting up video:', videoPath, error);
          // Create a fallback object with error message
          const errorMaterial = new Three.MeshStandardMaterial({
            color: 0xff0000,
            side: Three.DoubleSide,
          });
          const geometryVideo = new Three.PlaneGeometry(1, 1);
          const planeVideo = new Three.Mesh(geometryVideo, errorMaterial);

          planeVideo.name = _videoData._id;
          planeVideo.url = _videoData._id;
          planeVideo.asset_file_path = videoPath;
          planeVideo.deleteType = 'video';

          planeVideo.position.set(position_x, position_y, position_z);
          const fallbackMeshScale = videoSavedScaleToMeshScale(
            scale_x,
            scale_y,
            scale_z,
            1,
            1
          );
          planeVideo.scale.set(
            fallbackMeshScale.x,
            fallbackMeshScale.y,
            fallbackMeshScale.z
          );
          planeVideo.rotation.set(
            Three.MathUtils.degToRad(rotation_x),
            Three.MathUtils.degToRad(rotation_y),
            Three.MathUtils.degToRad(rotation_z)
          );

          planeVideo.userData.loadError = true;
          planeVideo.userData.errorMessage = error.message;
          planeVideo.events = _videoData?.events;

          scene.add(planeVideo);
          store.dispatch(setPushIntersectedObject(planeVideo));
          setPolyCount((prevCount) => prevCount + 1);
        });
    }
  });
}

export function duplicateVideoAsset(
  item,
  { Three, scene, transformControls, selectedObject, setPolyCount },
  setPendingUploads,
  originalMesh = null,
  options = {}
) {
  const attachToParent = options.attachToParent || null;
  const localTransform = options.localTransform || null;

  if (!item) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  if (attachToParent && originalMesh?.isObject3D) {
    const baseName = (
      originalMesh.file_name ||
      item.file_name ||
      originalMesh.name ||
      'Video'
    ).replace(/\.[^/.]+$/, '');
    const ext =
      (item.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      originalMesh.file_name?.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);
    const cloned = originalMesh.clone(true);
    cloned.uuid = Three.MathUtils.generateUUID();
    cloned.name = tempId;
    cloned.url = tempId;
    cloned._id = tempId;
    cloned.temp_file_name = tempId;
    cloned.file_name = duplicateFileName;
    cloned.deleteType = 'video';
    attachToParent.add(cloned);
    if (localTransform) {
      cloned.position.copy(localTransform.position);
      cloned.rotation.copy(localTransform.rotation);
      cloned.scale.copy(localTransform.scale);
    } else {
      cloned.position.copy(originalMesh.position);
      cloned.rotation.copy(originalMesh.rotation);
      cloned.scale.copy(originalMesh.scale);
    }
    cloned.updateMatrixWorld(true);
    store.dispatch(setPushIntersectedObject(cloned));
    store.dispatch(addSceneVideo(cloned));
    copyPhysicsToDuplicate(originalMesh, cloned);
    const parentId =
      attachToParent._id || attachToParent.name || attachToParent.url;
    const list = store.getState().assets_object?.assets_data?.video || [];
    const updated = [
      ...list,
      {
        _id: tempId,
        id: tempId,
        name: tempId,
        file_name: duplicateFileName,
        parent: parentId,
        deleteType: 'video',
      },
    ];
    store.dispatch(update_assets_data({ type: 'video', data: updated }));
    return cloned;
  }

  // Resolve file from item, passed mesh, or mesh in scene (Redux may not persist File references)
  const sceneMesh =
    scene && (item._id != null || item.url != null || item.name != null)
      ? scene.getObjectByName(item._id || item.url || item.name)
      : null;
  const meshWithFile = originalMesh || sceneMesh;
  const file =
    item.localFile ||
    item.originalFile ||
    meshWithFile?.localFile ||
    meshWithFile?.originalFile;

  // When we have a local File (from upload), use handleVideoUpload logic
  if (file && typeof file === 'object' && 'size' in file && 'name' in file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error(
        () => (
          <div className="flex justify-between items-center">
            <span>File size should not exceed 10MB.</span>
          </div>
        ),
        {
          duration: 3000,
          style: { background: '#1d1e21', color: '#fff' },
        }
      );
      return;
    }
    selectedObject = null;
    store.dispatch(selected_object(null));
    transformControls?.detach();
    store.dispatch(setLoadingMessage(true));

    const objectURL = URL.createObjectURL(file);
    // Use item.file_name if available (handles renamed videos), otherwise fall back to file.name
    const nameToUse = item.file_name || item.asset_file_name || file.name;
    const baseName = nameToUse.replace(/\.[^/.]+$/, '');
    // Get extension from item.file_name first, then fall back to file extension
    const ext =
      (item.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      nameToUse.match(/\.[^/.]+$/)?.[0] ||
      file.name.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);

    const videoElement = document.createElement('video');
    videoElement.src = objectURL;
    videoElement.autoplay = false;
    videoElement.crossOrigin = 'anonymous';
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.preload = 'auto';
    videoElement.load();

    videoElement.addEventListener('loadeddata', async () => {
      const videoW = videoElement.videoWidth;
      const videoH = videoElement.videoHeight;
      const aspectRatio = videoW / videoH;
      const { planeWidth, planeHeight } = calculateVideoPlaneDimensions(
        aspectRatio,
        item.video_settings?.aspect_ratio ?? DEFAULT_VIDEO_ASPECT_MODE
      );

      const videoTexture = new Three.VideoTexture(videoElement);
      videoTexture.minFilter = Three.LinearFilter;
      videoTexture.magFilter = Three.LinearFilter;
      videoTexture.generateMipmaps = false;

      const material = new Three.MeshStandardMaterial({
        map: videoTexture,
        side: Three.DoubleSide,
      });
      const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
      const videoMesh = new Three.Mesh(geometry, material);
      videoMesh.castShadow = true;
      videoMesh.receiveShadow = true;
      store.dispatch(setVideoScales({ x: planeWidth, y: planeHeight }));

      videoMesh.rotation.order = 'XYZ';
      videoMesh.rotation.set(0, 0, 0);
      videoMesh.scale.set(1, 1, 1);

      videoMesh.name = tempId;
      videoMesh._id = tempId;
      videoMesh.url = tempId;
      videoMesh.objPath = file;
      videoMesh.file_name = duplicateFileName;
      videoMesh.deleteType = 'video';
      videoMesh.asset_file_path = objectURL;
      videoMesh.originalFile = file;
      videoMesh.localFile = file;
      videoMesh.temp_file_name = tempId;
      videoMesh.videoElement = videoElement;
      videoMesh.videoTexture = videoTexture;

      videoMesh.userData = {
        file_name: duplicateFileName,
        video_settings: {
          video_source: objectURL,
          play_on_awake: true,
        },
      };

      videoMesh.dispose = function () {
        if (this.videoElement) {
          this.videoElement.pause();
          this.videoElement.src = '';
          this.videoElement.load();
        }
        if (this.videoTexture) {
          this.videoTexture.dispose();
        }
        if (this.material) {
          this.material.dispose();
        }
        if (this.geometry) {
          this.geometry.dispose();
        }
      };

        const sourceMesh = originalMesh || scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);

        if (setPendingUploads) {
          setPendingUploads((prev) => [
            ...prev,
            {
              file: file,
              transform: {
                position_x: sourceMesh?.position?.x || 0,
                position_y: sourceMesh?.position?.y || 0,
                position_z: sourceMesh?.position?.z || 0,
                rotation_x: Three.MathUtils.radToDeg(sourceMesh?.rotation?.x || 0),
                rotation_y: Three.MathUtils.radToDeg(sourceMesh?.rotation?.y || 0),
                rotation_z: Three.MathUtils.radToDeg(sourceMesh?.rotation?.z || 0),
                scale_x: sourceMesh?.scale?.x || 1,
                scale_y: sourceMesh?.scale?.y || 1,
                scale_z: sourceMesh?.scale?.z || 1,
              },
              tempId: tempId,
            },
          ]);
        }

        videoMesh.position.set(
          sourceMesh?.position?.x || 0,
          sourceMesh?.position?.y || 0,
          sourceMesh?.position?.z || 0
        );
        videoMesh.rotation.copy(sourceMesh?.rotation || new Three.Euler());
        videoMesh.scale.copy(sourceMesh?.scale || new Three.Vector3(1, 1, 1));

      store.dispatch(setPushIntersectedObject(videoMesh));
      scene.add(videoMesh);
      videoMesh.objectURL = objectURL;

      selectedObject = videoMesh;
      if (setPolyCount) setPolyCount((prev) => prev + 1);
      selectObjectInScene(tempId);
      toast.success('Video added to scene successfully');

      store.dispatch(addSceneVideo(videoMesh));
      store.dispatch(addVideo({ url: objectURL, name: duplicateFileName }));
      store.dispatch(add_new_assets({ type: 'video', data: videoMesh }));
      store.dispatch(set_is_video_editoropen(true));
      store.dispatch(selected_object(videoMesh));
      store.dispatch(setLoadingMessage(false));
    });

    videoElement.addEventListener('error', (e) => {
      // If the error occurred because the src was cleared (intentional cleanup), ignore it
      if (!videoElement.src || videoElement.src === window.location.href) {
        return;
      }
      toast.error('Failed to load video file');
      store.dispatch(setLoadingMessage(false));
    });
    return;
  }

  if (!item.asset_file_path || !item.file_name) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  // Clear current selection
  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls?.detach();

  // Set loading state
  store.dispatch(setLoadingMessage(true));

  // Generate unique properties for duplicate
  const nameToUse = item.file_name || item.asset_file_name || 'video';
  const baseName = nameToUse.replace(/\.[^/.]+$/, ''); // strip extension
  const duplicateFileName =
    getNextCopyName(scene, baseName) +
    (nameToUse.match(/\.[^/.]+$/)?.[0] || '');

  const tempId = buildTempFileName(duplicateFileName);
  const duplicateVideoCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;

  // Create video element
  const video = document.createElement('video');
  video.src = item.asset_file_path;
  video.crossOrigin = 'anonymous';
  video.loop = item.video_settings?.loop || true;
  video.muted = item.video_settings?.mute || true;
  video.volume = item.video_settings?.volume || 0.5;
  video.load();

  video.addEventListener('loadeddata', () => {
    // Calculate video dimensions
    const videoW = video.videoWidth;
    const videoH = video.videoHeight;
    const aspectRatio = videoW / videoH;
    const { planeWidth, planeHeight } = calculateVideoPlaneDimensions(
      aspectRatio,
      item.video_settings?.aspect_ratio ?? DEFAULT_VIDEO_ASPECT_MODE
    );

    // Create video texture
    const videoTexture = new Three.VideoTexture(video);
    videoTexture.minFilter = Three.LinearFilter;
    videoTexture.magFilter = Three.LinearFilter;
    videoTexture.generateMipmaps = false;

    // Create material with video texture
    const material = new Three.MeshStandardMaterial({
      map: videoTexture,
      side: Three.DoubleSide,
    });

    // Create geometry and mesh
    const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
    const duplicatedMesh = new Three.Mesh(geometry, material);
    duplicatedMesh.castShadow = true;
    duplicatedMesh.receiveShadow = true;

    // Set initial orientation
    duplicatedMesh.rotation.order = 'XYZ';
    duplicatedMesh.rotation.set(0, Math.PI, 0); // Rotate 180 degrees around Y to face camera

    // Set basic properties
    duplicatedMesh.name = tempId;
    duplicatedMesh.url = tempId;
    duplicatedMesh.deleteType = 'video';
    duplicatedMesh.asset_file_path = item.asset_file_path;
    // Remove typo field to avoid confusion; rely on asset_file_path or set asset_file_pathh only when required by publisher
    duplicatedMesh.asset_file_pathh = item.asset_file_path;
    duplicatedMesh.videoElement = video;
    duplicatedMesh.videoTexture = videoTexture;
    duplicatedMesh.file_name = duplicateFileName;
    // Apply transform properties from item with slight offset to avoid overlap
    // Prioritize scene object (tempMesh or originalMesh) for current transform
    const sourceMesh = originalMesh || scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);

    if (sourceMesh) {
      duplicatedMesh.position.set(
        sourceMesh.position.x,
        sourceMesh.position.y,
        sourceMesh.position.z
      );
      duplicatedMesh.rotation.copy(sourceMesh.rotation);
      duplicatedMesh.scale.copy(sourceMesh.scale);
    } else {
      duplicatedMesh.position.set(
        item.position_x || 0,
        item.position_y || 0,
        item.position_z || 0
      );
      duplicatedMesh.rotation.set(
        Three.MathUtils.degToRad(item.rotation_x || 0),
        Three.MathUtils.degToRad(item.rotation_y || 0),
        Three.MathUtils.degToRad(item.rotation_z || 0)
      );
      const itemMeshScale = videoSavedScaleToMeshScale(
        item.scale_x,
        item.scale_y,
        item.scale_z,
        planeWidth,
        planeHeight
      );
      duplicatedMesh.scale.set(
        itemMeshScale.x,
        itemMeshScale.y,
        itemMeshScale.z
      );
    }
    // Add cleanup method to the mesh
    duplicatedMesh.dispose = function () {
      if (this.videoElement) {
        this.videoElement.pause();
        this.videoElement.src = '';
        this.videoElement.load();
      }
      if (this.videoTexture) {
        this.videoTexture.dispose();
      }
      if (this.material) {
        this.material.dispose();
      }
      if (this.geometry) {
        this.geometry.dispose();
      }
    };

    // Store all item properties in userData
    duplicatedMesh.userData = {
      video_settings: { ...(item.video_settings || {}) },
      project: item.project,
      user: item.user,
      asset_file_name: item.asset_file_name,
      file_name: duplicateFileName,
      asset_file_type: item.asset_file_type,
      asset_file_path: item.asset_file_path,
      published: item.published,
      asset_thumbnail: item.asset_thumbnail,
      position_x: duplicatedMesh.position.x,
      position_y: duplicatedMesh.position.y,
      position_z: duplicatedMesh.position.z,
      scale_x: duplicatedMesh.scale.x,
      scale_y: duplicatedMesh.scale.y,
      scale_z: duplicatedMesh.scale.z,
      rotation_x: item.rotation_x || 0,
      rotation_y: item.rotation_y || 0,
      rotation_z: item.rotation_z || 0,
      events: [...(item.events || [])], // Copy events array
      saved: false, // Mark as unsaved since it's a duplicate
      video_code: duplicateVideoCode,
      originalId: item._id || item.id, // Reference to original item
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    // Handle pending uploads if needed
    if (setPendingUploads && originalMesh?.originalFile) {
      setPendingUploads((prev) => [
        ...prev,
        {
          file: originalMesh.originalFile,
          transform: {
            position_x: duplicatedMesh.position.x,
            position_y: duplicatedMesh.position.y,
            position_z: duplicatedMesh.position.z,
            rotation_x: item.rotation_x || 0,
            rotation_y: item.rotation_y || 0,
            rotation_z: item.rotation_z || 0,
            scale_x: duplicatedMesh.scale.x,
            scale_y: duplicatedMesh.scale.y,
            scale_z: duplicatedMesh.scale.z,
          },
          tempId: tempId,
          itemData: duplicatedMesh.userData, // Include all item data
        },
      ]);
    }

    // Add to scene
    scene.add(duplicatedMesh);

    // Update Redux store
    store.dispatch(setVideoScales({ x: planeWidth, y: planeHeight }));
    store.dispatch(setPushIntersectedObject(duplicatedMesh));
    store.dispatch(addSceneVideo(duplicatedMesh));
    copyPhysicsToDuplicate(sourceMesh || originalMesh || item, duplicatedMesh);
    store.dispatch(
      addVideo({ url: item.asset_file_path, name: duplicateFileName })
    );

    // // Show success message
    // toast.success(`Video duplicated successfully as "${duplicateFileName}"`);
    const displayName =
      tempId.length > 30 ? `${tempId.substring(0, 30)}...` : tempId;
    toast.success(`Particle duplicated as "${displayName}"`);

    // Update Redux store with new asset - include all original properties
    const duplicateItemData = {
      ...item,
      _id: tempId,
      id: tempId,
      temp_file_name: tempId,
      file_name: duplicateFileName,
      video_code: duplicateVideoCode,
      position_x: duplicatedMesh.position.x,
      position_y: duplicatedMesh.position.y,
      position_z: duplicatedMesh.position.z,
      parent: undefined,
      saved: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      originalId: item._id || item.id,
      isDuplicate: true,
    };

    store.dispatch(
      add_new_assets({
        type: 'video',
        data: duplicatedMesh,
        itemData: duplicateItemData,
      })
    );
    store.dispatch(set_is_video_editoropen(true));
    store.dispatch(selected_object(duplicatedMesh));
    store.dispatch(setLoadingMessage(false));

    // Attempt to autoplay if needed
    if (item.video_settings?.play_on_awake) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {})
          .catch((error) => {
            console.warn(
              'Autoplay prevented for duplicated video:',
              item.asset_file_path,
              error
            );
          });
      }
    }
  });

  video.addEventListener('error', (error) => {
    console.error('Error duplicating video:', error);
    toast.error('Failed to duplicate video asset');
    store.dispatch(setLoadingMessage(false));
  });
}

export function cleanupVideoResources(videoMesh) {
  if (videoMesh && typeof videoMesh.dispose === 'function') {
    videoMesh.dispose();
  }
}
